/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from 'react'

import { styled } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import MDBox from 'components/MDBox'
import { useAuth } from 'context/auth'
import { updateRoutes, useMaterialUIController } from 'context/materialUI'
import FeedbackPage from 'layouts/feedback/internal'
import ChangeLog from 'modules/ChangeLog'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import ManageCard from 'modules/ManageCard'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import { ROUTE_PATHS, RouteType } from 'routes'

import MainForm from './components/MainForm'
import { FormContext, initMemberForm } from './constants'
import { OrganisationMember } from './types'

const PrintableBox = styled(MDBox)(({ theme }) => ({
	'@media print': {
		marginTop: theme.spacing(5),
		marginRight: theme.spacing(2),
		marginLeft: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
}))

function OrganisationMemberPage() {
	const { user } = useAuth()
	const contentRef = useRef<HTMLDivElement>(null)
	const reactToPrintFn = useReactToPrint({ contentRef })

	const queryClient = useQueryClient()

	const [controller, dispatch] = useMaterialUIController()
	const { routes } = controller
	const [formEdited, setFormEdited] = useState<boolean>(false)
	const [localForm, setLocalForm] = useState<OrganisationMember>(initMemberForm)

	const navigate = useNavigate()
	const location = useLocation()
	const locationPathNameParts = location.pathname.split('/')
	const orgMemberId = locationPathNameParts[locationPathNameParts.length - 1]
	const isNew = orgMemberId === 'new'

	const fetchData = async () => {
		const response = await axios.get<OrganisationMember>(
			`${process.env.REACT_APP_API_URL}/organisation-member`,
			{
				params: {
					member_id: orgMemberId,
					timeframe: localForm.analytics?.timeframe,
					feedbackType: localForm.analytics?.feedbackType,
				},
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const saveNewOrgMember = async () => {
		const response = await axios.post<{ id: string }>(
			`${process.env.REACT_APP_API_URL}/organisation-member`,
			{ ...localForm },
			{
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const updateOrgMember = async () => {
		const response = await axios.put<OrganisationMember>(
			`${process.env.REACT_APP_API_URL}/organisation-member`,
			{ ...localForm },
			{
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const deleteOrgMember = async () => {
		await axios.delete(`${process.env.REACT_APP_API_URL}/organisation-member`, {
			params: {
				member_id: localForm.details.id,
			},
			headers: {
				Authorization: await user.auth.getIdToken(),
			},
		})
	}

	const { data, error, isLoading, refetch } = useQuery({
		queryKey: [`org-member-${orgMemberId}`],
		queryFn: fetchData,
		enabled: !isNew,
	})

	useEffect(() => {
		if (!isNew && data) {
			setLocalForm(data)

			// Collect existing route keys for quick lookup
			const existingKeys = new Set(routes.map(r => r.key))

			// Filter out feedback routes that already exist in routes
			const feedbackRoutes = data.feedbackTable.table.rows
				.filter(feedback => !existingKeys.has(`feedback-${feedback.id}`))
				.map(feedback => ({
					type: 'invisible' as const,
					name: `Feedback: ${feedback.id}`,
					key: `feedback-${feedback.id}`,
					route: `${ROUTE_PATHS.FEEDBACK.BASE}/${feedback.id}`,
					component: <FeedbackPage />,
					authRequired: true,
				}))

			// Only update routes if new feedback routes exist
			if (feedbackRoutes.length > 0) {
				updateRoutes(dispatch, routes, feedbackRoutes)
			}
		} else {
			setLocalForm(initMemberForm)
		}
	}, [data, isNew, dispatch, routes])

	useEffect(() => {
		if (!isNew) {
			refetch()
		}
	}, [localForm.analytics?.timeframe, localForm.analytics?.feedbackType, isNew, refetch])

	return (
		<FormContext.Provider value={{ localForm, setLocalForm, formEdited, setFormEdited, isNew }}>
			<DashboardLayout>
				<DashboardNavbar isMini />
				<MDBox mt={4}>
					<Grid container spacing={3}>
						<Grid item xs={12} lg={9}>
							{isNew || (data && !isLoading && !error) ? (
								<PrintableBox mb={3} ref={contentRef}>
									<MainForm />
								</PrintableBox>
							) : (
								<LoadingComponent type="box" />
							)}
						</Grid>
						<Grid item xs={12} lg={3}>
							{isNew || (data && !isLoading && !error) ? (
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<ManageCard
											formEdited={formEdited}
											setFormEdited={setFormEdited}
											save={{
												disabled: user.data.role === 'VIEWER',
												onClicked: async () => {
													if (isNew) {
														const newOrgMember = await saveNewOrgMember()
														const newRoute: RouteType[] = [
															{
																type: 'invisible',
																name: `Organisation Member: ${newOrgMember.id}`,
																key: `org-member-${newOrgMember.id}`,
																route: `${ROUTE_PATHS.ORGANISATION_MEMBER.BASE}/${newOrgMember.id}`,
																component: <OrganisationMemberPage />,
																authRequired: true,
															},
														]
														updateRoutes(dispatch, routes, newRoute)
														navigate(`${ROUTE_PATHS.ORGANISATION_MEMBER.BASE}/${newOrgMember.id}`)
													} else {
														await updateOrgMember()
														refetch()
													}
												},
											}}
											print={{ onClicked: reactToPrintFn }}
											deleteItem={{
												visible: !isNew,
												onClicked: async () => {
													await deleteOrgMember()
													queryClient.invalidateQueries(['organisation-members-table'])
													navigate(ROUTE_PATHS.ORGANISATION_MEMBER.BASE)
													updateRoutes(
														dispatch,
														routes,
														routes.filter(r => r.key !== `organisation-member-${orgMemberId}`)
													)
												},
											}}
										/>
									</Grid>
									{!isNew && (
										<Grid item xs={12}>
											<ChangeLog log={data?.changeLog} />
										</Grid>
									)}
								</Grid>
							) : (
								<LoadingComponent type="box" height={'30rem'} />
							)}
						</Grid>
					</Grid>
				</MDBox>
			</DashboardLayout>
		</FormContext.Provider>
	)
}

export default OrganisationMemberPage
